/**
 * Puts saved draft data or an assistant sync result into the wizard's Redux so the manual
 * steps show exactly what the chat built — the same slice the wizard fills as
 * the user clicks through (contactInfo/contactInfoForm, eventInfo/eventInfoForm,
 * sessionsOfEvent). The docs come straight from the sync response (already
 * saved by the backend), so nothing is re-PUT here; what the sync could not
 * save yet (incomplete contact/event) is placed in the step forms as typed
 * values, and the caller jumps to `resumeStep`.
 *
 * Shared by Event Partials and the assistant. Opening a saved draft only reads
 * sessions and hydrates state; explicit saves still go through backend gates.
 */
import type { AssistantDraft, AssistantSyncResult, SavedDoc } from "../../../../api/eventAssistant";
import {
  CREATE_SESSION_STEP1_SUCCESS,
  SET_CONTACT_INFO_FORM,
  SET_EVENT_INFO_FORM,
  SET_LAST_STEP_LOADING,
  UPDATE_CONTACT_INFO_SUCCESS,
  UPDATE_EVENT_INFO_SUCCESS,
} from "../../../../store/constants/createEvent";
import {
  getSessionssOfSpecificEvent,
  resetCreateEventSlice,
  setFormAsPartialEventAction,
  toggleEditButtonTimelineAction,
} from "../../../../store/actions/createEvent";
import { phoneNumberFormat } from "../../../../utils/methods/formatters";

type BillingRow = { name?: string; email?: string; mobile?: string };

/** Redux dispatch as the wizard uses it (plain actions and thunks). */
type WizardDispatch = (action: unknown) => unknown;

interface ContactDoc extends SavedDoc {
  firstName?: string;
  lastName?: string;
  mobile?: string;
  isClientSelectedFromDropdown?: boolean;
  isSameAddress?: boolean;
  clientDetails?: { companyAddress?: string; COIAddress?: string; emails?: unknown };
}

interface EventDoc extends SavedDoc {
  PONumber?: string;
  venueAddressLine1?: string;
  venueAddressLine2?: string;
  venueCity?: string;
  venueState?: string;
  venueZipCode?: string;
  contactInformationId?: string | { _id?: string };
}

interface SessionDoc {
  venueAddressLine1?: string;
  venueAddressLine2?: string;
  venueCity?: string;
  venueState?: string;
  venueZipCode?: string;
  [key: string]: unknown;
}

type WizardHydrationState = Pick<AssistantSyncResult,
  "eventId" | "contactInformation" | "event" | "resumeStep"
> & Partial<Pick<AssistantSyncResult, "draft" | "contactSaved" | "eventSaved">>;

const billingToForm = (rows: BillingRow[] | undefined, type: string) =>
  (rows || []).map((r) => ({
    name: r.name || "",
    [`${type}Email`]: r.email || "",
    [`${type}Mobile`]: phoneNumberFormat(r.mobile),
  }));

/** Draft contactInfo (not saved yet) → the step-1 form values, in BOTH shapes the form reads. */
export const draftContactToForm = (c: AssistantDraft["contactInfo"] = {}) => {
  const emails = {
    estimateEmails: billingToForm(c.billingEmails?.estimate, "estimate"),
    invoiceEmails: billingToForm(c.billingEmails?.invoice, "invoice"),
    depositInvoiceEmails: billingToForm(c.billingEmails?.depositInvoice, "depositInvoice"),
  };
  const isSameAddress = c.isSameAddress !== false;
  return {
    firstName: c.firstName || "",
    lastName: c.lastName || "",
    phone: phoneNumberFormat(c.mobile),
    mobile: c.mobile || "",
    email: c.email || "",
    company: c.company || "",
    companyAddress: c.companyAddress || "",
    isSameAddress,
    COIAddress: isSameAddress ? "" : c.COIAddress || "",
    emails,
    // ContactInformation.tsx hydrates companyAddress/COIAddress/emails from
    // clientDetails.* at mount — keep the saved-doc shape alongside.
    clientDetails: {
      companyAddress: c.companyAddress || "",
      COIAddress: isSameAddress ? "" : c.COIAddress || "",
      emails,
    },
    isClientSelectedFromDropdown: false,
  };
};

/** Draft eventInfo (not saved yet) → the step-2 form values (form aliases). */
export const draftEventToForm = (e: AssistantDraft["eventInfo"] = {}) => ({
  eventName: e.eventName || "",
  eventStart: e.eventStart || null,
  eventEnd: e.eventEnd || null,
  venue: e.venue || "",
  address1: e.venueAddressLine1 || "",
  address2: e.venueAddressLine2 || "",
  city: e.venueCity || "",
  state: e.venueState || "",
  zip: e.venueZipCode || "",
  location: { latitude: "", longitude: "" },
});

/** Saved contact doc → the formData the wizard keeps next to it. */
export const contactDocToForm = (doc: ContactDoc) => ({
  ...doc,
  firstName: doc?.firstName || "",
  lastName: doc?.lastName || "",
  phone: phoneNumberFormat(doc?.mobile),
  isClientSelectedFromDropdown: doc?.isClientSelectedFromDropdown ?? false,
  isSameAddress: doc?.isSameAddress || false,
  clientDetails: {
    companyAddress: doc?.clientDetails?.companyAddress,
    COIAddress: doc?.clientDetails?.COIAddress,
    emails: doc?.clientDetails?.emails,
  },
});

/** Saved event doc → form aliases, preserving the server's workflow state. */
export const eventDocToForm = (doc: EventDoc) => ({
  ...doc,
  poNumber: doc?.PONumber,
  address1: doc?.venueAddressLine1,
  address2: doc?.venueAddressLine2,
  city: doc?.venueCity,
  state: doc?.venueState,
  zip: doc?.venueZipCode,
  contactInformationId:
    typeof doc?.contactInformationId === "object"
      ? doc?.contactInformationId?._id
      : doc?.contactInformationId,
});

const hasAny = (obj: object | undefined) =>
  !!obj && Object.values(obj).some((v) => v !== undefined && v !== null && v !== "");

/** Resume an already-read draft without updating its contact or event. */
export async function hydrateWizardFromSavedEvent(
  dispatch: WizardDispatch,
  event: SavedDoc,
  contactInformation: SavedDoc,
): Promise<number> {
  if (!event?._id || !contactInformation?._id) {
    throw new Error("The draft's event or contact information could not be loaded. Please refresh the list and try again.");
  }
  return hydrateWizardFromSync(dispatch, {
    eventId: event._id, event, contactInformation, resumeStep: 4,
    contactSaved: true, eventSaved: true,
  });
}

/**
 * Applies a sync result to the wizard's Redux and resolves with the step to
 * resume on. Safe to call for a partial sync: whatever was not saved is
 * pre-filled from the draft instead.
 */
export async function hydrateWizardFromSync(
  dispatch: WizardDispatch,
  result: WizardHydrationState,
): Promise<number> {
  const { contactInformation, event, eventId, draft, resumeStep, contactSaved, eventSaved } =
    result;

  dispatch(resetCreateEventSlice());

  if (contactInformation?._id) {
    // A doc returned with contactSaved=false is the previously saved one: keep
    // its id (so the wizard updates instead of creating) but show the draft's
    // values in the form — that is what the chat has and what step 1 must fix.
    const formData =
      contactSaved === false && hasAny(draft?.contactInfo)
        ? { ...contactDocToForm(contactInformation as ContactDoc), ...draftContactToForm(draft.contactInfo) }
        : contactDocToForm(contactInformation as ContactDoc);
    dispatch({
      type: UPDATE_CONTACT_INFO_SUCCESS,
      payload: { res: { data: contactInformation }, formData },
    });
  } else if (hasAny(draft?.contactInfo)) {
    dispatch({
      type: SET_CONTACT_INFO_FORM,
      payload: { contactInfoForm: draftContactToForm(draft.contactInfo) },
    });
  }

  if (!event?._id) {
    if (hasAny(draft?.eventInfo)) {
      dispatch({
        type: SET_EVENT_INFO_FORM,
        payload: { eventInfoForm: draftEventToForm(draft.eventInfo) },
      });
    }
    return resumeStep;
  }

  // A saved draft event: same flags Event Partials sets when resuming one.
  dispatch(setFormAsPartialEventAction());
  dispatch(toggleEditButtonTimelineAction(true));
  const eventFormData =
    eventSaved === false && hasAny(draft?.eventInfo)
      ? { ...eventDocToForm(event as EventDoc), ...draftEventToForm(draft.eventInfo) }
      : eventDocToForm(event as EventDoc);
  dispatch({
    type: UPDATE_EVENT_INFO_SUCCESS,
    payload: { res: { data: event }, formData: eventFormData },
  });

  // Stop loading on every outcome, but only allow navigation after a successful
  // authorized read. A failed load must never look like an empty saved event.
  dispatch({ type: SET_LAST_STEP_LOADING, payload: true });
  try {
    const loaded = await dispatch(
      getSessionssOfSpecificEvent(eventId, ({ response }: { response?: { data?: { data?: SessionDoc[] } } }) => {
        const first = response?.data?.data?.[0];
        if (!first) return;
        const withAliases = {
          ...first,
          address1: first?.venueAddressLine1,
          address2: first?.venueAddressLine2,
          city: first?.venueCity,
          state: first?.venueState,
          zip: first?.venueZipCode,
        };
        dispatch({
          type: CREATE_SESSION_STEP1_SUCCESS,
          payload: { formData: withAliases, res: { data: withAliases } },
        });
      }),
    ) as { response?: unknown; error?: unknown } | undefined;
    if (!loaded?.response || loaded.error) {
      throw loaded?.error || new Error("The event sessions could not be loaded. Please try again.");
    }
  } finally {
    dispatch({ type: SET_LAST_STEP_LOADING, payload: false });
  }
  return resumeStep;
}
